import { useState, useEffect, useCallback, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { useAuth } from './useAuth';
import { api } from '../utils/api';
import { useChatSocket } from '../context/ChatSocketContext';

export interface ChatMessage {
  id: string;
  content: string;
  senderId: string;
  receiverId: string;
  isRead: boolean;
  createdAt: string;
  attachments?: Array<{
    id: string;
    url: string;
    name: string;
    type: string;
    size?: number;
    duration?: number;
  }>;
  sender?: {
    id: string;
    prenom: string;
    nom: string;
    photoProfil: string | null;
  };
}

export interface Conversation {
  user: {
     id: string;
     prenom: string;
     nom: string;
     photoProfil: string | null;
  };
  lastMessage: ChatMessage;
  unreadCount: number;
}

export function useDirectChat(receiverId?: string) {
  const { user, token } = useAuth();
  const { socket, isConnected } = useChatSocket();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [presence, setPresence] = useState<{ status: 'online' | 'offline', lastSeen?: string } | null>(null);
  const userId = user?.id;

  const fetchHistory = useCallback(async (targetId: string) => {
    try {
      const res = await api.get(`/direct-chat/${targetId}`);
      console.log(`useDirectChat: History loaded for ${targetId}:`, res);
      
      const data = res?.data || res;
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching chat history:', err);
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.get('/direct-chat/conversations');
      const data = res?.data || res;
      const convs = Array.isArray(data) ? data : [];
      
      console.log('useDirectChat: Loaded', convs.length, 'conversations:', convs.map(c => ({
        userId: c.user.id,
        userName: `${c.user.prenom} ${c.user.nom}`
      })));
      
      setConversations(convs);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  }, []);

  useEffect(() => {
    if (!socket || !userId) return;

    const handleNewMessage = (message: ChatMessage) => {
      console.log('useDirectChat: New socket message received:', message);
      if (
        (message.senderId === userId && message.receiverId === receiverId) ||
        (message.senderId === receiverId && message.receiverId === userId)
      ) {
        setMessages(prev => {
           const filtered = prev.filter(m => !m.id.startsWith('temp-') || m.content !== message.content);
           if (filtered.find(m => m.id === message.id)) return filtered;
           return [...filtered, message];
        });
      }
      fetchConversations();
    };

    const handleTyping = (data: { senderId: string, isTyping: boolean }) => {
      if (data.senderId === receiverId) setIsTyping(data.isTyping);
    };

    const handlePresence = (data: { userId: string, status: 'online' | 'offline', lastSeen?: string }) => {
      if (data.userId === receiverId) setPresence({ status: data.status, lastSeen: data.lastSeen });
    };

    const handleRead = (data: { readerId: string }) => {
      if (data.readerId === receiverId) setMessages(prev => prev.map(m => ({ ...m, isRead: true })));
    };

    socket.on('new_direct_message', handleNewMessage);
    socket.on('user_typing', handleTyping);
    socket.on('user_presence', handlePresence);
    socket.on('messages_read', handleRead);

    if (receiverId) {
      socket.emit('check_user_status', receiverId, (res: any) => setPresence(res));
    }

    return () => {
      socket.off('new_direct_message', handleNewMessage);
      socket.off('user_typing', handleTyping);
      socket.off('user_presence', handlePresence);
      socket.off('messages_read', handleRead);
    };
  }, [socket, receiverId, userId, fetchConversations]);


  useEffect(() => {
    if (receiverId) {
      fetchHistory(receiverId);
    }
  }, [receiverId, fetchHistory]);

  useEffect(() => {
    if (token && userId) {
      fetchConversations();
    }
  }, [token, userId, fetchConversations]);

  const markAsRead = useCallback(() => {
    if (socket && receiverId) {
      socket.emit('mark_as_read', receiverId);
    }
  }, [socket, receiverId]);

  const sendMessage = useCallback((content: string, attachments?: any[]) => {
    console.log('useDirectChat: Request to send message:', content, 'to:', receiverId, 'as:', userId, 'attachments:', attachments?.length);
    
    if (socket && receiverId && userId) {
      const optimisticMsg: ChatMessage = {
        id: `temp-${Date.now()}`,
        content,
        senderId: userId,
        receiverId,
        isRead: false,
        createdAt: new Date().toISOString(),
        attachments: attachments || []
      };

      console.log('useDirectChat: Adding optimistic message:', optimisticMsg.id);
      setMessages(prev => [...prev, optimisticMsg]);
      
      console.log('useDirectChat: Emitting send_direct_message...');
      socket.emit('send_direct_message', {
        content,
        receiverId,
        attachments
      });
    }
  }, [socket, receiverId, userId]);

  const sendTypingStatus = useCallback((typing: boolean) => {
    if (socket && receiverId) {
      socket.emit('typing', {
        receiverId,
        isTyping: typing,
      });
    }
  }, [socket, receiverId]);

  return {
    messages,
    conversations,
    isTyping,
    isConnected,
    presence,
    markAsRead,
    sendMessage,
    sendTypingStatus,
    fetchConversations,
    userId,
  };
}
